import { useState, useEffect, useRef, Fragment } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';

interface KakurasuPuzzle {
  rowTargets: number[];
  colTargets: number[];
  solution: boolean[][];
  seed: number;
  size: number;
}

type CellState = 0 | 1 | 2; // 0: vacío, 1: pintado, 2: marcado vacío

export default function KakurasuGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();

  const [puzzle, setPuzzle] = useState<KakurasuPuzzle | null>(null);
  const [boardState, setBoardState] = useState<CellState[][]>([]);
  const [history, setHistory] = useState<CellState[][][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [startTime] = useState(Date.now());
  const [moves, setMoves] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [modoAsistido, setModoAsistido] = useState(false);
  const [showIndices, setShowIndices] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();

  // Detectar si viene de una sesión
  const sesionRemota = searchParams.get('sesion_remota');
  const sesionJuego = searchParams.get('sesion_juego');
  const sesionPresencial = searchParams.get('sesion');
  const isInSession = !!(sesionRemota && sesionJuego) || !!(sesionPresencial && sesionJuego);

  const dificultadOrden = parseInt(dificultad || '1');
  const isFirstLoad = useRef(true);
  const [forceReload, setForceReload] = useState(0);

  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [boardW, setBoardW] = useState(0);
  const [boardH, setBoardH] = useState(0);

  const measureBoard = () => {
    if (boardAreaRef.current) {
      const { clientWidth, clientHeight } = boardAreaRef.current;
      setBoardW(clientWidth - 16);
      setBoardH(clientHeight - 16);
    }
  };

  useEffect(() => {
    if (!loading) requestAnimationFrame(measureBoard);
  }, [loading]);

  useEffect(() => {
    window.addEventListener('resize', measureBoard);
    return () => window.removeEventListener('resize', measureBoard);
  }, []);
  const { saveProgress: saveGameProgress, loading: progressLoading, nivelId } = useGameProgress('kakurasu', dificultadOrden);

  // Mapeo de dificultad a tamaño
  const getSizeFromDificultad = (orden: number): number => {
    const sizeMap: Record<number, number> = {
      1: 4,  // Neurona
      2: 5,  // Sinapsis
      3: 6,  // Cerebro
      4: 8,  // Genio
      5: 9,  // Einstein
    };
    return sizeMap[orden] || 4;
  };

  // Cargar puzzle
  useEffect(() => {
    const loadPuzzle = async () => {
      try {
        setLoading(true);
        setError('');

        const size = getSizeFromDificultad(dificultadOrden);
        const seedParam = searchParams.get('seed') ? parseInt(searchParams.get('seed')!) : undefined;
        const isNewGame = forceReload > 0;
        const response = await api.get('/kakurasu/generate', {
          params: { size, ...(!isNewGame && seedParam !== undefined && { seed: seedParam }) }
        });

        const puzzleData: KakurasuPuzzle = response.data.data;
        setPuzzle(puzzleData);
        setSearchParams({ seed: puzzleData.seed.toString() }, { replace: true });

        // Inicializar tablero vacío
        const emptyBoard: CellState[][] = Array(puzzleData.size)
          .fill(0)
          .map(() => Array(puzzleData.size).fill(0));

        setBoardState(emptyBoard);
        setHistory([JSON.parse(JSON.stringify(emptyBoard))]);
        setHistoryIndex(0);
        setMoves(0);
        setIsCompleted(false);
        setShowVictory(false);
      } catch (err: any) {
        console.error('Error al cargar puzzle:', err);
        setError(err.response?.data?.error || 'Error al cargar el puzzle');
      } finally {
        setLoading(false);
        isFirstLoad.current = false;
      }
    };

    loadPuzzle();
  }, [dificultadOrden, forceReload]);

  // Guardar estado en historial
  const saveState = (newState: CellState[][]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newState)));

    // Limitar historial a 50 estados
    if (newHistory.length > 50) {
      newHistory.shift();
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    } else {
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  };

  // Toggle celda: 0 -> 1 -> 2 -> 0
  const handleCellClick = (r: number, c: number) => {
    if (isCompleted || !puzzle) return;

    const newState = boardState.map(row => [...row]);
    newState[r][c] = ((newState[r][c] + 1) % 3) as CellState;

    setBoardState(newState);
    setMoves(prev => prev + 1);
    saveState(newState);

    // Verificar victoria automáticamente
    checkVictory(newState, false);
  };

  // Deshacer
  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setBoardState(JSON.parse(JSON.stringify(history[historyIndex - 1])));
    }
  };

  // Rehacer
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setBoardState(JSON.parse(JSON.stringify(history[historyIndex + 1])));
    }
  };

  // Reiniciar
  const handleReset = () => {
    if (!puzzle || !window.confirm('¿Estás seguro de que quieres reiniciar el tablero?')) return;

    const emptyBoard: CellState[][] = Array(puzzle.size)
      .fill(0)
      .map(() => Array(puzzle.size).fill(0));

    setBoardState(emptyBoard);
    setHistory([JSON.parse(JSON.stringify(emptyBoard))]);
    setHistoryIndex(0);
    setMoves(0);
    setIsCompleted(false);
    setShowVictory(false);
    setError('');
  };

  // Ver solución
  const handleShowSolution = () => {
    if (!puzzle || !window.confirm('¿Estás seguro de que quieres ver la solución? Esto no guardará tu progreso.')) return;

    const solutionState: CellState[][] = puzzle.solution.map(row =>
      row.map(cell => (cell ? 1 : 0) as CellState)
    );

    setBoardState(solutionState);
    saveState(solutionState);
    setError('Solución revelada. No se guardará este progreso.');
  };

  // Dar pista
  const handleHint = () => {
    if (!puzzle) return;

    const errors: { r: number; c: number }[] = [];
    const missing: { r: number; c: number }[] = [];

    // Buscar errores y celdas faltantes
    for (let r = 0; r < puzzle.size; r++) {
      for (let c = 0; c < puzzle.size; c++) {
        if (boardState[r][c] === 1 && !puzzle.solution[r][c]) {
          errors.push({ r, c });
        } else if (boardState[r][c] !== 1 && puzzle.solution[r][c]) {
          missing.push({ r, c });
        }
      }
    }

    const newState = boardState.map(row => [...row]);

    if (errors.length > 0) {
      const error = errors[Math.floor(Math.random() * errors.length)];
      newState[error.r][error.c] = 0;
      setBoardState(newState);
      saveState(newState);
      setError('Se ha corregido una casilla incorrecta');
    } else if (missing.length > 0) {
      const miss = missing[Math.floor(Math.random() * missing.length)];
      newState[miss.r][miss.c] = 1;
      setBoardState(newState);
      setMoves(prev => prev + 1);
      saveState(newState);
      setError('');
      checkVictory(newState, false);
    } else {
      setError('¡El tablero parece estar perfecto!');
    }
  };

  // Verificar victoria
  const checkVictory = async (state: CellState[][], manual: boolean) => {
    if (!puzzle) return;

    let won = true;

    // Verificar filas
    for (let r = 0; r < puzzle.size; r++) {
      let sum = 0;
      for (let c = 0; c < puzzle.size; c++) {
        if (state[r][c] === 1) sum += (c + 1);
      }
      if (sum !== puzzle.rowTargets[r]) won = false;
    }

    // Verificar columnas
    for (let c = 0; c < puzzle.size; c++) {
      let sum = 0;
      for (let r = 0; r < puzzle.size; r++) {
        if (state[r][c] === 1) sum += (r + 1);
      }
      if (sum !== puzzle.colTargets[c]) won = false;
    }

    if (won && !isCompleted) {
      setIsCompleted(true);
      const tiempo = Math.floor((Date.now() - startTime) / 1000);

      // Guardar progreso
      await saveProgress(tiempo, moves);

      // Mensaje especial si está en sesión
      if (isInSession) {
        setError('✅ ¡Completado! Progreso guardado en tu sesión.');
      }

      // Mostrar modal de victoria
      setTimeout(() => setShowVictory(true), isInSession ? 500 : 0);
    } else if (manual && !won) {
      setError('Aún no es correcto. Sigue intentando.');
    }
  };

  // Guardar progreso
  const saveProgress = async (tiempo: number, movimientos: number) => {
    if (!puzzle) return;

    // Esperar a que se cargue el nivel_id
    if (progressLoading || !nivelId) {
      toast.error('Error al guardar progreso. Por favor, intenta de nuevo.');
      return;
    }

    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: puzzle.seed,
        tiempo,
        movimientos,
        completado: true,
      });

      // Si viene de una sesión, guardar también en la sesión
      if (sesionJuego) {
        try {
          await api.post(`/sessions/games/${sesionJuego}/result`, {
            tiempo,
            movimientos,
            completado: true,
            datos_juego: {
              tablero_id: puzzle.seed,
              size: puzzle.size,
            },
          });
        } catch (sessionError) {
          console.error('Error al guardar en sesión:', sessionError);
          // No bloqueamos el flujo si falla el guardado en sesión
        }
      }
    } catch (error) {
      console.error('Error al guardar progreso:', error);
    }
  };

  // ─── Modo asistido ────────────────────────────────────────────────────────
  function calcularErrores(state: CellState[][], puz: KakurasuPuzzle): Set<string> {
    const errores = new Set<string>();
    const sz = puz.size;

    for (let r = 0; r < sz; r++) {
      let sum = 0;
      for (let c = 0; c < sz; c++) {
        if (state[r][c] === 1) sum += (c + 1);
      }
      if (sum > puz.rowTargets[r]) {
        for (let c = 0; c < sz; c++) {
          if (state[r][c] === 1) errores.add(`${r},${c}`);
        }
      }
    }

    for (let c = 0; c < sz; c++) {
      let sum = 0;
      for (let r = 0; r < sz; r++) {
        if (state[r][c] === 1) sum += (r + 1);
      }
      if (sum > puz.colTargets[c]) {
        for (let r = 0; r < sz; r++) {
          if (state[r][c] === 1) errores.add(`${r},${c}`);
        }
      }
    }

    return errores;
  }

  // Calcular sumas actuales
  const getCurrentSums = () => {
    if (!puzzle) return { rowSums: [], colSums: [] };

    const rowSums: number[] = Array(puzzle.size).fill(0);
    const colSums: number[] = Array(puzzle.size).fill(0);

    for (let r = 0; r < puzzle.size; r++) {
      for (let c = 0; c < puzzle.size; c++) {
        if (boardState[r][c] === 1) {
          rowSums[r] += (c + 1);
          colSums[c] += (r + 1);
        }
      }
    }

    return { rowSums, colSums };
  };

  const { rowSums, colSums } = getCurrentSums();
  const errores = modoAsistido ? calcularErrores(boardState, puzzle) : new Set<string>();

  // Celdas rectangulares: +2 por las cabeceras de targets e índices en cada eje
  const totalCols = (puzzle?.size ?? 6) + 2;
  const gap = 4, pad = 4;
  const cellW = boardW > 0 ? Math.floor((boardW - pad * 2 - gap * (totalCols - 1)) / totalCols) : 50;
  const cellH = boardH > 0 ? Math.floor((boardH - pad * 2 - gap * (totalCols - 1)) / totalCols) : 50;
  const minCell = Math.min(cellW, cellH);
  const fontSize = Math.floor(minCell * 0.45);
  const fontSizeSmaller = Math.floor(minCell * 0.35);
  const fontSizeLarger = Math.floor(minCell * 0.65);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Cargando...</div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">❌ Error al cargar puzzle</div>
      </div>
    );
  }

  const difficulties = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  return (
    <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex flex-col">
      {isInSession && (
        <div className="bg-green-600 text-white text-center text-sm py-1 px-3">
          {sesionPresencial ? 'Sesion Presencial Activa' : 'Sesion Remota'} — Tu progreso se guardara automaticamente
        </div>
      )}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🔢 Kakurasu</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map(d => (
            <button
              key={d.orden}
              onClick={() => {
                if (dificultadOrden === d.orden) setForceReload(prev => prev + 1);
                else navigate(`/juegos/kakurasu/${d.orden}`);
              }}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                dificultadOrden === d.orden
                  ? 'bg-white text-purple-700'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} {d.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          {/* Undo */}
          <button onClick={handleUndo} disabled={historyIndex <= 0} title="Deshacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </button>
          {/* Redo */}
          <button onClick={handleRedo} disabled={historyIndex >= history.length - 1} title="Rehacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
            </svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          {/* Hint */}
          <button onClick={handleHint} disabled={isCompleted} title="Pista"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </button>
          {/* Solution */}
          <button onClick={handleShowSolution} disabled={isCompleted} title="Ver solucion"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          {/* Reset */}
          <button onClick={handleReset} title="Reiniciar"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          {/* New board */}
          <button onClick={() => { setSearchParams({}); setForceReload(prev => prev + 1); }} title="Nuevo tablero"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
          {/* Toggle índices */}
          <button onClick={() => setShowIndices(v => !v)}
            title={showIndices ? 'Ocultar ayuda de índices' : 'Mostrar ayuda de índices'}
            className={`p-1.5 rounded transition-all text-white ${showIndices ? 'bg-white/20 hover:bg-white/30' : 'bg-yellow-500/70 ring-2 ring-yellow-300'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
            </svg>
          </button>
          {/* How to play */}
          <button onClick={() => setShowHowToPlay(true)} title="Como se juega"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-3 mt-2 p-2 bg-blue-500/20 border border-blue-400/50 text-blue-100 rounded text-sm">
          {error}
        </div>
      )}

      {/* Board Area */}
      <div ref={boardAreaRef} className="flex-1 min-h-0 flex items-center justify-center p-2">
        <div
          className="kakurasu-board inline-grid bg-gray-800 p-1 rounded-lg"
          style={{
            gridTemplateColumns: `${cellW}px repeat(${puzzle.size}, ${cellW}px) ${cellW}px`,
            gridTemplateRows: `${cellH}px repeat(${puzzle.size}, ${cellH}px) ${cellH}px`,
            gap: `${gap}px`,
            padding: `${pad}px`,
          }}
        >
          {/* Top row: empty | col targets | empty */}
          <div />
          {puzzle.colTargets.map((target, c) => {
            const match = colSums[c] === target;
            const over = colSums[c] > target;
            return (
              <div
                key={`ct-${c}`}
                className={`flex items-center justify-center font-bold rounded transition-all ${
                  match ? 'text-white bg-green-600'
                  : over ? 'text-white bg-red-600'
                  : 'text-white bg-gray-600'
                }`}
                style={{ width: cellW, height: cellH, fontSize: fontSize, fontWeight: 800 }}
              >
                {target}
              </div>
            );
          })}
          <div />

          {/* Board rows */}
          {Array.from({ length: puzzle.size }, (_, r) => (
            <Fragment key={`row-${r}`}>
              <div
                className={`flex items-center justify-center font-bold rounded transition-all ${
                  rowSums[r] === puzzle.rowTargets[r] ? 'text-white bg-green-600'
                  : rowSums[r] > puzzle.rowTargets[r] ? 'text-white bg-red-600'
                  : 'text-white bg-gray-600'
                }`}
                style={{ width: cellW, height: cellH, fontSize: fontSize, fontWeight: 800 }}
              >
                {puzzle.rowTargets[r]}
              </div>

              {Array.from({ length: puzzle.size }, (_, c) => {
                const state = boardState[r][c];
                const hasError = errores.has(`${r},${c}`);
                return (
                  <div
                    key={`cell-${r}-${c}`}
                    onClick={() => handleCellClick(r, c)}
                    className={`cursor-pointer rounded transition-all flex items-center justify-center ${
                      hasError ? 'bg-red-500 shadow-inner'
                      : state === 1 ? 'bg-purple-600 shadow-inner'
                      : state === 2 ? 'bg-gray-200'
                      : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    style={{ width: cellW, height: cellH }}
                  >
                    {state === 2 && (
                      <span className="text-gray-500 font-bold" style={{ fontSize: fontSizeLarger }}>✕</span>
                    )}
                  </div>
                );
              })}

              <div
                className="flex items-center justify-center font-semibold text-gray-500"
                style={{ width: cellW, height: cellH, fontSize: fontSizeSmaller, opacity: showIndices ? 1 : 0 }}
              >
                {r + 1}
              </div>
            </Fragment>
          ))}

          {/* Bottom row: empty | col indices | empty */}
          <div />
          {Array.from({ length: puzzle.size }, (_, c) => (
            <div
              key={`ci-${c}`}
              className="flex items-center justify-center font-semibold text-gray-500"
              style={{ width: cellW, height: cellH, fontSize: fontSizeSmaller, opacity: showIndices ? 1 : 0 }}
            >
              {c + 1}
            </div>
          ))}
          <div />
        </div>
      </div>

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Kakurasu"
        gameIcon="🔢"
        objective="Pinta las casillas correctas para que las sumas coincidan con los números objetivo en los bordes."
        rules={[
          'Cada casilla del tablero tiene un valor: las columnas se numeran de izquierda a derecha (1, 2, 3...) y las filas de arriba a abajo (1, 2, 3...)',
          'Al pintar una casilla en la fila R y columna C: sumas el valor de la columna (C) a esa fila, y sumas el valor de la fila (R) a esa columna',
          'Los números de la izquierda indican la suma objetivo para cada fila',
          'Los números de arriba indican la suma objetivo para cada columna',
          'Debes pintar las casillas correctas para que todas las sumas coincidan',
        ]}
        howToPlay="Click en una casilla para alternar: Vacío → Pintado → Marcado (✕) → Vacío"
        author="Otto Janko"
      />

      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{
          tiempo: Math.floor((Date.now() - startTime) / 1000),
          movimientos: moves,
        }}
      />
    </div>
  );
}
